/**
 * FinancialDashboard - Dashboard principal do módulo Financeiro
 * 
 * Container que gerencia dashboards financeiros via sistema de abas.
 * Segue o mesmo padrão arquitetural do CrmDashboard.
 * 
 * @author Sistema
 * @version 1.0.0
 * RELEASE: 2026-03-19 v1.0.0 - Criação do dashboard financeiro
 */

import { useQueryClient } from '@tanstack/react-query';
import { Loader2, LayoutDashboard, RefreshCw, Clock, X } from 'lucide-react';
import { useModuleDashboards } from '@/hooks/useDashboards';
import { DashboardTabs, DashboardRenderer } from '@/components/crm/dashboard';
import { Button } from '@/components/ui/button';
import { useCrmOverviewFilters } from '@/hooks/useCrmOverviewFilters';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { cn } from '@/lib/utils';
import {
  CrmPeriodFilter,
} from '@/components/crm/filters';

/**
 * Página principal do Dashboard Financeiro
 * Integra o sistema de abas com renderização dinâmica de dashboards
 */
export default function FinancialDashboard() {
  const queryClient = useQueryClient();
  
  // Hook para refresh com timestamp
  const { isRefreshing, formattedTime, triggerRefresh } = useRefreshWithTimestamp();
  
  // Hook para gerenciar dashboards disponíveis
  const {
    dashboards,
    selectedDashboard,
    selectedDashboardId,
    selectDashboard,
    isLoading,
  } = useModuleDashboards('financial');

  // Hook para gerenciar filtros do dashboard (reutilizando padrão CRM)
  const {
    filters,
    setPeriodPreset,
    setCustomPeriod,
    clearFilters,
    hasActiveFilters
  } = useCrmOverviewFilters();

  // Função para atualizar dados do dashboard
  const handleRefresh = async () => {
    await triggerRefresh(async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['financial-dashboard-stats'] }),
        queryClient.invalidateQueries({ queryKey: ['user-dashboards'] }),
      ]);
    });
  };

  // Dashboards já filtrados pelo hook useModuleDashboards('financial')

  // Estado de carregamento inicial
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Sem dashboards financeiros disponíveis
  if (dashboards.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">
            Dashboard Financeiro
          </h1>
        </div>
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <LayoutDashboard className="h-16 w-16 mb-4 opacity-20" />
          <p className="text-lg font-medium">
            Nenhum dashboard disponível
          </p>
          <p className="text-sm">
            Entre em contato com o administrador para configurar seus dashboards.
          </p>
        </div>
      </div>
    );
  }

  // Seleciona o dashboard financeiro correto
  const activeDashboard = dashboards.find(d => d.id === selectedDashboardId) || dashboards[0];

  return (
    <div className="space-y-4">
      {/* Cabeçalho: Título + Refresh | Filtros (scroll) */}
      <div className="flex items-center gap-2">
        {/* Esquerda: Título + Refresh (não encolhe) */}
        <div className="flex items-center gap-1.5 shrink-0">
          <h1 className="text-lg md:text-2xl font-bold tracking-tight">
            Dashboard Financeiro
          </h1>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            title="Atualizar dados"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", (isRefreshing || isLoading) && "animate-spin")} />
          </Button>
          <span className="text-[10px] hidden md:inline-flex items-center gap-1 text-muted-foreground">
            <Clock className="h-2.5 w-2.5" />
            {formattedTime}
          </span>
        </div>

        {/* Separador visual */}
        <span className="text-muted-foreground/30 shrink-0">|</span>

        {/* Centro: Filtros com scroll horizontal */}
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none min-w-0 flex-1">
          <CrmPeriodFilter
            value={filters.period}
            onPresetChange={setPeriodPreset}
            onCustomChange={setCustomPeriod}
            className="h-[22px] text-xs px-2"
          />
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-[22px] px-1.5 text-xs text-muted-foreground hover:text-foreground shrink-0"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Tabs de navegação entre dashboards financeiros (se houver mais de 1) */}
      <DashboardTabs
        dashboards={dashboards}
        selectedDashboardId={activeDashboard?.id || null}
        onSelectDashboard={selectDashboard}
        isLoading={isLoading}
      />

      {/* Conteúdo do dashboard selecionado - passa filtros globais */}
      {activeDashboard && (
        <DashboardRenderer
          componentKey={activeDashboard.component_key}
          dashboardId={activeDashboard.id}
          filters={filters}
        />
      )}
    </div>
  );
}
